// Pure helpers for the role pane of the Add / Edit Staff dialog (BLU-64).
// Kept free of Angular so the rules can be unit tested on their own.

// what a role links to; roles not listed here have nothing to link
export type LinkKind = 'classes' | 'grades' | 'gate';

const LINK_KIND_BY_ROLE: { [role: string]: LinkKind } = {
  teacher: 'classes',
  supervisor: 'grades',
  security: 'gate',
  shared_gate_scanner: 'gate',
};

export function linkKindFor(role: string): LinkKind | null {
  return LINK_KIND_BY_ROLE[role] || null;
}

export interface SchoolClass {
  id: number;
  name: string;
}

export interface GradeWithClasses {
  id: number;
  name: string;
  school_classes?: SchoolClass[];
}

export type CheckState = 'none' | 'some' | 'all';

// a grade's checkbox: none, some or all of its current classes are picked
export function gradeCheckState(grade: GradeWithClasses, selectedClassIds: Set<number>): CheckState {
  const classes = grade.school_classes || [];
  const picked = classes.filter(clazz => selectedClassIds.has(clazz.id)).length;
  if (picked === 0) {
    return 'none';
  }
  return picked === classes.length ? 'all' : 'some';
}

export interface LinkChip {
  // 'grade:<id>' removes every class of the grade, 'class:<id>' / 'gate:<id>' one item
  key: string;
  label: string;
}

// a grade with all of its classes picked shows as one chip, otherwise one chip per picked class
export function classChips(grades: GradeWithClasses[], selectedClassIds: Set<number>): LinkChip[] {
  const chips: LinkChip[] = [];
  grades.forEach(grade => {
    const classes = grade.school_classes || [];
    if (classes.length > 0 && gradeCheckState(grade, selectedClassIds) === 'all') {
      chips.push({ key: `grade:${grade.id}`, label: grade.name });
      return;
    }
    classes
      .filter(clazz => selectedClassIds.has(clazz.id))
      .forEach(clazz => chips.push({ key: `class:${clazz.id}`, label: clazz.name }));
  });
  return chips;
}

export function gradeChips(grades: GradeWithClasses[], selectedGradeIds: Set<number>): LinkChip[] {
  return grades
    .filter(grade => selectedGradeIds.has(grade.id))
    .map(grade => ({ key: `grade:${grade.id}`, label: grade.name }));
}

// search: a matching grade keeps all its classes, otherwise only the matching classes are kept
export function filterGradeTree(grades: GradeWithClasses[], search: string): GradeWithClasses[] {
  const term = (search || '').trim().toLowerCase();
  if (!term) {
    return grades;
  }
  const result: GradeWithClasses[] = [];
  grades.forEach(grade => {
    if ((grade.name || '').toLowerCase().includes(term)) {
      result.push(grade);
      return;
    }
    const classes = (grade.school_classes || []).filter(clazz => (clazz.name || '').toLowerCase().includes(term));
    if (classes.length > 0) {
      result.push({ ...grade, school_classes: classes });
    }
  });
  return result;
}

export function filterByName<T extends { name?: string }>(items: T[], search: string): T[] {
  const term = (search || '').trim().toLowerCase();
  return term ? items.filter(item => (item.name || '').toLowerCase().includes(term)) : items;
}

export function sameIds(a: Iterable<number>, b: Iterable<number>): boolean {
  const left = new Set(a);
  const right = new Set(b);
  if (left.size !== right.size) {
    return false;
  }
  for (const id of left) {
    if (!right.has(id)) {
      return false;
    }
  }
  return true;
}

// CLDR plural category (Arabic has zero / one / two / few / many / other)
export function pluralCategory(lang: string, count: number): string {
  try {
    return new Intl.PluralRules(lang === 'ar' ? 'ar' : 'en').select(count);
  } catch {
    return count === 1 ? 'one' : 'other';
  }
}
